import { useState, useCallback } from "react";
import { useQuote } from "@/hooks/useQuote";
import { TemplateSelector } from "@/components/TemplateSelector";
import { QuoteEditor } from "@/components/QuoteEditor";
import { QuoteList } from "@/components/QuoteList";
import { exportQuotePdf } from "@/lib/exportPdf";
import { TooltipProvider } from "@/components/ui/tooltip";
import "./index.css";

type View = "templates" | "editor" | "list";

export function App() {
	const [view, setView] = useState<View>("templates");
	const {
		quotes,
		activeQuote,
		activeQuoteId,
		setActiveQuoteId,
		createQuote,
		updateQuote,
		updateClient,
		updateCompany,
		addLineItem,
		updateLineItem,
		removeLineItem,
		addTerm,
		removeTerm,
		setSignature,
		deleteQuote,
		duplicateQuote,
		calculateTotals,
		aiSuggestions,
		loadingAi,
		fetchPricingSuggestions,
		fetchTermsSuggestions,
		fetchDescriptionSuggestion,
		dismissSuggestion,
		clearSuggestions,
	} = useQuote();

	const handleSelectTemplate = useCallback(
		(templateId: string) => {
			createQuote(templateId);
			setView("editor");
		},
		[createQuote]
	);

	const handleBack = useCallback(() => {
		if (quotes.length > 0) {
			setView("list");
		} else {
			setView("templates");
		}
	}, [quotes.length]);

	const handleSelectQuote = useCallback(
		(id: string) => {
			setActiveQuoteId(id);
			setView("editor");
		},
		[setActiveQuoteId]
	);

	const handleNewQuote = useCallback(() => {
		setView("templates");
	}, []);

	const handleDuplicate = useCallback(() => {
		if (activeQuoteId) {
			duplicateQuote(activeQuoteId);
		}
	}, [activeQuoteId, duplicateQuote]);

	const handleExportPdf = useCallback(async () => {
		if (!activeQuote) return;
		// Temporarily switch to preview to render the preview element
		const previewTab = document.querySelector('[data-state="inactive"][value="preview"]') as HTMLButtonElement;
		const editTab = document.querySelector('[data-state="active"][value="edit"]') as HTMLButtonElement;
		const wasOnEdit = !!editTab;

		if (previewTab && wasOnEdit) {
			previewTab.click();
			await new Promise((r) => setTimeout(r, 300));
		}

		try {
			const filename = `${activeQuote.quoteNumber}-${activeQuote.title.replace(/\s+/g, "-").toLowerCase()}`;
			await exportQuotePdf("quote-preview", filename);
		} catch {
			// If preview isn't visible, just alert
			alert("Please switch to the Preview tab first, then export.");
		}

		if (wasOnEdit && editTab) {
			const editTabButton = document.querySelector('[value="edit"]') as HTMLButtonElement;
			if (editTabButton) editTabButton.click();
		}
	}, [activeQuote]);

	return (
		<TooltipProvider>
			<div className="min-h-screen bg-[hsl(220,16%,6%)]">
				{view === "templates" && <TemplateSelector onSelect={handleSelectTemplate} />}
				{view === "list" && (
					<QuoteList
						quotes={quotes}
						onSelect={handleSelectQuote}
						onDelete={deleteQuote}
						onDuplicate={duplicateQuote}
						onNewQuote={handleNewQuote}
					/>
				)}
				{view === "editor" && activeQuote && (
					<QuoteEditor
						quote={activeQuote}
						totals={calculateTotals}
						aiSuggestions={aiSuggestions}
						loadingAi={loadingAi}
						onUpdateQuote={updateQuote}
						onUpdateClient={updateClient}
						onUpdateCompany={updateCompany}
						onAddLineItem={addLineItem}
						onUpdateLineItem={updateLineItem}
						onRemoveLineItem={removeLineItem}
						onAddTerm={addTerm}
						onRemoveTerm={removeTerm}
						onSetSignature={setSignature}
						onFetchPricingSuggestions={fetchPricingSuggestions}
						onFetchTermsSuggestions={fetchTermsSuggestions}
						onFetchDescriptionSuggestion={fetchDescriptionSuggestion}
						onDismissSuggestion={dismissSuggestion}
						onClearSuggestions={clearSuggestions}
						onBack={handleBack}
						onDuplicate={handleDuplicate}
						onExportPdf={handleExportPdf}
					/>
				)}
			</div>
		</TooltipProvider>
	);
}

export default App;
